export class Displacer {
  static cssVarPrefix = '--lps--displace-offset';
  static offsetKeys = ['right', 'left', 'bottom', 'top'];

  constructor() {
    this.cache = {
      right: 0,
      left: 0,
      bottom: 0,
      top: 0,
    };
    this.documentStyle = document.documentElement.style;

    // Define reactive offsets property
    this.offsets = {};
    Displacer.offsetKeys.forEach((edge) => {
      Object.defineProperty(this.offsets, edge, {
        enumerable: true,
        get: () => this.cache[edge],
        set: (value) => {
          if (value !== this.cache[edge]) {
            this.documentStyle.setProperty(`${Displacer.cssVarPrefix}--${edge}`, `${value}px`);
          }
          this.cache[edge] = value;
        },
      });
    });

    // Bind methods
    this.displace = this.displace.bind(this);
    this.calculateOffset = this.calculateOffset.bind(this);

    // Initial calculation and event listeners
    this.displace();
    window.addEventListener('scroll', this.debounce(() => this.displace(), 0));
    window.addEventListener('resize', this.debounce(() => this.displace(), 200));
  }

  /**
   * Calculates the raw offset of a given element relative to the viewport or document, based on the specified edge.
   *
   * @param {Element} el - The DOM element for which to calculate the offset.
   * @param {'top'|'left'|'right'|'bottom'} edge - The edge of the element to measure ('top', 'left', 'right', or 'bottom').
   * @returns {number} The offset value in pixels for the specified edge.
   */
  getRawOffset(el, edge) {
    let displacement = 0;
    const horizontal = edge === 'left' || edge === 'right';
    let placement = el.getBoundingClientRect()[horizontal ? 'left' : 'top'];
    placement -= window[`scroll${horizontal ? 'X' : 'Y'}`] || document.documentElement[`scroll${horizontal ? 'Left' : 'Top'}`] || 0;

    switch (edge) {
      case 'top':
      case 'left':
      case 'right':
        displacement = placement;
        break;
      case 'bottom':
        displacement = el.getBoundingClientRect().bottom;
        break;
      default:
        displacement = 0;
    }
    return displacement;
  }

  /**
   * Calculates the maximum offset for a given edge by checking all elements
   * with a class name matching `js-offset-{edge}`. Only visible elements are considered.
   *
   * @param {string} edge - The edge to calculate the offset for (e.g., 'top', 'bottom', 'left', 'right').
   * @returns {number} The maximum offset value found among the relevant elements.
   */
  calculateOffset(edge) {
    let edgeOffset = 0;
    const displacingElements = document.getElementsByClassName(`js-offset-${edge}`);
    Array.from(displacingElements).forEach((el) => {
      if (window.getComputedStyle(el).display === 'none') return;
      let displacement = this.getRawOffset(el, edge);
      edgeOffset = Math.max(edgeOffset, displacement);
    });
    return edgeOffset;
  }

  /**
   * Calculates and updates the offsets for each edge, optionally broadcasting the changes.
   *
   * @param {boolean} [broadcast=true] - Whether to dispatch a 'wpViewportOffsetChange' event with the updated offsets.
   * @returns {Object} The updated offsets for each edge.
   */
  displace(broadcast = true) {
    const newOffsets = {};
    Displacer.offsetKeys.forEach((edge) => {
      newOffsets[edge] = this.calculateOffset(edge);
    });
    Displacer.offsetKeys.forEach((edge) => {
      this.offsets[edge] = newOffsets[edge];
    });
    if (broadcast) {
      document.dispatchEvent(new CustomEvent('wpViewportOffsetChange', { detail: this.offsets }));
    }
    return this.offsets;
  }

  /**
   * Creates and returns a debounced version of the provided function that delays its execution
   * until after a specified wait time has elapsed since the last time it was invoked.
   *
   * @param {Function} func - The function to debounce.
   * @param {number} wait - The number of milliseconds to delay.
   * @returns {Function} A debounced version of the original function.
   */
  debounce(func, wait) {
    let timeout;
    return function (...args) {
      const later = () => {
        timeout = null;
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}
